import React, { useState, useEffect, useContext } from "react";
import { toast } from "react-toastify";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import api from "../../services/api";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import Typography from "@material-ui/core/Typography";
import Chip from "@material-ui/core/Chip";
import Grid from "@material-ui/core/Grid";
import usePlans from "../../hooks/usePlans";

import moment from "moment";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
}));

const Invoices = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const { getPlanCompany } = usePlans();

  const [isCompanyExpired, setIsCompanyExpired] = useState(false);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [loadingPlan, setLoadingPlan] = useState(false);
  const [crmInvoices, setCrmInvoices] = useState([]);
  const [crmPendency, setCrmPendency] = useState({ hasPendency: false, dueDate: null });
  const [loadingCrmInvoices, setLoadingCrmInvoices] = useState(false);
  const [crmCheckedAt, setCrmCheckedAt] = useState(null);
  const [crmCooldownUntil, setCrmCooldownUntil] = useState(0);
  const [nowTick, setNowTick] = useState(Date.now());
  const [monitoringPaused, setMonitoringPaused] = useState(false);
  const [nextCrmCheckAt, setNextCrmCheckAt] = useState(0);

  // Verificar se a empresa está vencida
  useEffect(() => {
    if (user && user.company) {
      const hoje = moment();
      const vencimento = moment(user.company.dueDate);
      const isExpired = hoje.isAfter(vencimento);
      setIsCompanyExpired(isExpired);
    }
  }, [user]);

  // Buscar informações do plano atual da empresa
  useEffect(() => {
    const fetchCurrentPlan = async () => {
      if (user && user.companyId) {
        setLoadingPlan(true);
        try {
          const planData = await getPlanCompany({}, user.companyId);
          setCurrentPlan(planData);
        } catch (err) {
          console.error("Erro ao buscar plano atual:", err);
          toastError(err);
        } finally {
          setLoadingPlan(false);
        }
      }
    };

    fetchCurrentPlan();
  }, [user]); // Removendo getPlanCompany das dependências para evitar loop infinito

  const fetchCrmInvoicesRef = React.useRef(null);
  const forceCrmCheckRef = React.useRef(null);

  useEffect(() => {
    if (!user?.companyId) return;

    let cancelled = false;

    const fetchCrmInvoices = async () => {
      setLoadingCrmInvoices(true);
      try {
        const { data } = await api.get("/companies/crm-invoices");
        if (cancelled) return;
        setCrmInvoices(Array.isArray(data.invoices) ? data.invoices : []);
        setCrmCheckedAt(data.checkedAt || null);
        setCrmPendency((prev) => {
          if (prev.hasPendency && !data.hasPendency) {
            toast.success("Pendência regularizada! Atualizando acesso...");
            setTimeout(() => window.location.reload(), 1500);
          }
          return {
            hasPendency: !!data.hasPendency,
            dueDate: data.dueDate || null
          };
        });
      } catch (err) {
        console.error("Erro ao buscar faturas do CRM:", err);
      } finally {
        if (!cancelled) setLoadingCrmInvoices(false);
      }
    };

    const forceCrmCheck = async () => {
      try {
        setCrmCooldownUntil(Date.now() + 20000);
        setNextCrmCheckAt(Date.now() + 300000);
        await api.post("/companies/billing-check-company");
        if (!cancelled) await fetchCrmInvoices();
      } catch (err) {
        console.error("Erro ao forçar re-check CRM:", err);
      }
    };

    fetchCrmInvoicesRef.current = fetchCrmInvoices;
    forceCrmCheckRef.current = forceCrmCheck;

    fetchCrmInvoices();

    const startedAt = Date.now();
    const MAX_MONITORING_MS = 10 * 60 * 1000;
    const POLL_INTERVAL_MS = 300000;
    setNextCrmCheckAt(Date.now() + POLL_INTERVAL_MS);

    const interval = setInterval(() => {
      if (Date.now() - startedAt >= MAX_MONITORING_MS) {
        setMonitoringPaused(true);
        setNextCrmCheckAt(0);
        clearInterval(interval);
        return;
      }
      setCrmPendency((current) => {
        if (current.hasPendency) forceCrmCheck();
        return current;
      });
      setNextCrmCheckAt(Date.now() + POLL_INTERVAL_MS);
    }, POLL_INTERVAL_MS);

    const tickInterval = setInterval(() => setNowTick(Date.now()), 1000);

    return () => {
      cancelled = true;
      clearInterval(interval);
      clearInterval(tickInterval);
    };
  }, [user]);

  const handleManualCheck = () => {
    if (forceCrmCheckRef.current && Date.now() >= crmCooldownUntil) {
      forceCrmCheckRef.current();
    }
  };

  return (
    <MainContainer>
      <MainHeader>
        <Title>Faturas</Title>
        {isCompanyExpired && (
          <div style={{ 
            backgroundColor: '#ffebee', 
            color: '#c62828', 
            padding: '10px', 
            borderRadius: '4px', 
            marginTop: '10px',
            border: '1px solid #ef9a9a'
          }}>
            <strong>Atenção:</strong> Sua assinatura está vencida. Entre em contato com o suporte para regularizar sua situação.
          </div>
        )}
      </MainHeader>

      {/* Seção do Plano Atual */}
      {currentPlan && currentPlan.plan && (
        <Card style={{ marginBottom: '16px' }}>
          <CardContent>
            <Typography variant="h6" style={{ marginBottom: '16px', color: '#1976d2' }}>
              📋 Plano Atual
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="subtitle2" color="textSecondary">
                  Nome do Plano
                </Typography>
                <Typography variant="h6">
                  {currentPlan.plan.name}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="subtitle2" color="textSecondary">
                  Valor Mensal
                </Typography>
                <Typography variant="h6" style={{ color: '#2e7d32' }}>
                  {currentPlan.plan.amount
                    ? currentPlan.plan.amount.toLocaleString('pt-br', { style: 'currency', currency: 'BRL' })
                    : 'R$ 0,00'
                  }
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <Typography variant="subtitle2" color="textSecondary">
                  Usuários
                </Typography>
                <Chip
                  label={currentPlan.plan.users || 0}
                  color="primary"
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <Typography variant="subtitle2" color="textSecondary">
                  Conexões
                </Typography>
                <Chip
                  label={currentPlan.plan.connections || 0}
                  color="secondary"
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <Typography variant="subtitle2" color="textSecondary">
                  Filas
                </Typography>
                <Chip
                  label={currentPlan.plan.queues || 0}
                  color="default"
                  size="small"
                />
              </Grid>
              {currentPlan.dueDate && (
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Próximo Vencimento
                  </Typography>
                  <Typography variant="body1" style={{
                    color: isCompanyExpired ? '#d32f2f' : '#1976d2',
                    fontWeight: 'bold'
                  }}>
                    {moment(currentPlan.dueDate).format("DD/MM/YYYY")}
                  </Typography>
                </Grid>
              )}
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="subtitle2" color="textSecondary">
                  Recorrência
                </Typography>
                <Typography variant="body1">
                  {currentPlan.recurrence || 'Não definida'}
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {loadingPlan && (
        <Card style={{ marginBottom: '16px' }}>
          <CardContent>
            <Typography variant="h6">
              Carregando informações do plano...
            </Typography>
          </CardContent>
        </Card>
      )}

      {(
        <>
          {monitoringPaused && (
            <div style={{
              backgroundColor: '#e3f2fd',
              color: '#0d47a1',
              padding: '12px 16px',
              borderRadius: '4px',
              marginTop: '24px',
              border: '1px solid #64b5f6',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '12px',
              flexWrap: 'wrap'
            }}>
              <span>
                <strong>Monitoramento pausado.</strong>{' '}
                Recarregue a página para continuar verificando o status no CRM.
              </span>
              <Button
                size="small"
                variant="outlined"
                color="primary"
                onClick={() => window.location.reload()}
              >
                Recarregar
              </Button>
            </div>
          )}
          {crmPendency.hasPendency && (
            <div style={{
              backgroundColor: '#fff4e5',
              color: '#663c00',
              padding: '12px 16px',
              borderRadius: '4px',
              marginTop: '24px',
              border: '1px solid #ffb74d'
            }}>
              <strong>Pendência no sistema de cobrança externo.</strong>{' '}
              {crmPendency.dueDate && (
                <>Vencimento mais próximo: {moment(crmPendency.dueDate).format("DD/MM/YYYY")}.{' '}</>
              )}
              Assim que o pagamento for registrado, o acesso será liberado automaticamente.
            </div>
          )}

          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginTop: '24px',
            marginBottom: '8px',
            gap: '12px',
            flexWrap: 'wrap'
          }}>
            <Typography variant="h6" style={{ margin: 0 }}>
              📄 Histórico de Faturas (Cobrança Externa)
            </Typography>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <Typography variant="caption" color="textSecondary">
                {crmCheckedAt
                  ? `Atualizado há ${Math.max(0, Math.floor((nowTick - new Date(crmCheckedAt).getTime()) / 1000))}s`
                  : 'Aguardando dados...'}
                {!monitoringPaused && nextCrmCheckAt > 0 && crmPendency.hasPendency && (() => {
                  const remaining = Math.max(0, Math.ceil((nextCrmCheckAt - nowTick) / 1000));
                  const mins = Math.floor(remaining / 60);
                  const secs = remaining % 60;
                  const label = mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
                  return ` · Próxima em ${label}`;
                })()}
              </Typography>
              <Button
                size="small"
                variant="outlined"
                color="primary"
                onClick={handleManualCheck}
                disabled={loadingCrmInvoices || nowTick < crmCooldownUntil}
              >
                {nowTick < crmCooldownUntil
                  ? `Aguarde ${Math.ceil((crmCooldownUntil - nowTick) / 1000)}s`
                  : 'Verificar agora'}
              </Button>
            </div>
          </div>

          <Paper className={classes.mainPaper} variant="outlined" style={{ marginBottom: '16px' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell align="center">Número</TableCell>
                  <TableCell align="center">Valor</TableCell>
                  <TableCell align="center">Data Venc.</TableCell>
                  <TableCell align="center">Status</TableCell>
                  <TableCell align="center">Ação</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {crmInvoices.map((inv) => {
                  const hoje = moment();
                  const venc = moment(inv.duedate);
                  const vencido = venc.isBefore(hoje, 'day');
                  const statusLabel = (() => {
                    if (inv.status === 2) return "Pago";
                    if (inv.status === 3) return "Parcialmente Pago";
                    if (inv.status === 4) return "Vencida";
                    if (inv.status === 5) return "Cancelada";
                    if (inv.status === 6) return "Rascunho";
                    if (inv.status === 1) return vencido ? "Vencida" : "Em Aberto";
                    return "Desconhecido";
                  })();
                  const isPendente = inv.status === 1 || inv.status === 3 || inv.status === 4;
                  const rowBg = isPendente && (vencido || inv.status === 4) ? '#ffbcbc9c' : undefined;
                  const canPay = isPendente && inv.id && inv.hash;
                  const payUrl = canPay
                    ? `https://crm.markesistemas.com.br/invoice/${inv.id}/${inv.hash}`
                    : null;
                  const detailUrl = inv.id && inv.hash
                    ? `https://crm.markesistemas.com.br/invoice/${inv.id}/${inv.hash}`
                    : null;
                  return (
                    <TableRow key={inv.id} style={{ backgroundColor: rowBg }}>
                      <TableCell align="center">
                        {detailUrl ? (
                          <a
                            href={detailUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: '#1976d2', textDecoration: 'underline' }}
                          >
                            {inv.formatted_number || inv.number || inv.id}
                          </a>
                        ) : (
                          inv.formatted_number || inv.number || inv.id
                        )}
                      </TableCell>
                      <TableCell style={{ fontWeight: 'bold' }} align="center">
                        {Number(inv.total || 0).toLocaleString('pt-br', { style: 'currency', currency: 'BRL' })}
                      </TableCell>
                      <TableCell align="center">{moment(inv.duedate).format("DD/MM/YYYY")}</TableCell>
                      <TableCell style={{ fontWeight: 'bold' }} align="center">{statusLabel}</TableCell>
                      <TableCell align="center">
                        {canPay ? (
                          <Button
                            size="small"
                            variant="outlined"
                            color="secondary"
                            href={payUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            PAGAR
                          </Button>
                        ) : (
                          <Button size="small" variant="outlined" disabled>
                            {inv.status === 2 ? "PAGO" : "—"}
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {!loadingCrmInvoices && crmInvoices.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      Nenhuma fatura no sistema de cobrança externo.
                    </TableCell>
                  </TableRow>
                )}
                {loadingCrmInvoices && <TableRowSkeleton columns={5} />}
              </TableBody>
            </Table>
          </Paper>
        </>
      )}

    </MainContainer>
  );
};

export default Invoices;
